import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/cn';

export interface CredentialDisplayProps {
  username: string;
  password: string;
  /** Warning above the values, explaining that this is the only sighting. */
  notice?: string;
  /**
   * What to call the password.
   *
   * Defaults to "Temporary", which is true of every account that is made to
   * change it on first sign-in. A job guest is not: there is no screen in the
   * customer portal to change a password on, so calling theirs temporary tells
   * them to go and do something they cannot do.
   */
  passwordLabel?: string;
}

/**
 * Shows a generated password once, with copy and reveal controls.
 *
 * The server only ever stores a hash, so this really is the single opportunity
 * to hand the credential over - the copy is masked by default so it is not
 * left on screen in an open-plan office or a screen share.
 */
export function CredentialDisplay({
  username,
  password,
  notice = 'This password is shown only once. Copy it now and pass it to the administrator securely.',
  passwordLabel = 'Temporary password',
}: CredentialDisplayProps) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="rounded-md border border-warning/30 bg-warning-soft p-3">
      <div className="flex items-start gap-2">
        <Icon name="key" size={15} className="mt-0.5 shrink-0 text-warning" />
        <p className="text-xs text-warning">{notice}</p>
      </div>

      <dl className="mt-3 space-y-2">
        <CopyRow label="Username" value={username} />
        <CopyRow label={passwordLabel} value={password} masked={!revealed} />
      </dl>

      <button
        type="button"
        onClick={() => {
          setRevealed((previous) => !previous);
        }}
        className="mt-2 inline-flex items-center gap-1.5 text-xs text-warning hover:underline"
      >
        <Icon name={revealed ? 'eye-off' : 'eye'} size={13} />
        {revealed ? 'Hide password' : 'Reveal password'}
      </button>
    </div>
  );
}

function CopyRow({
  label,
  value,
  masked = false,
}: {
  label: string;
  value: string;
  masked?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      /* Clipboard access can be denied (insecure origin, permissions policy).
         The value is on screen, so the user can still select it by hand. */
      setCopied(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <dt className="text-2xs text-warning/80">{label}</dt>
        <dd
          className={cn(
            'font-mono text-sm break-all text-content',
            masked && 'tracking-widest select-none',
          )}
        >
          {masked ? '•'.repeat(Math.min(value.length, 16)) : value}
        </dd>
      </div>

      <Button
        variant="secondary"
        size="sm"
        iconLeft={copied ? 'check' : 'copy'}
        onClick={() => {
          void copy();
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
